import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  Search, Calendar, MapPin, Users, 
  Plus, Eye, Trash2, Globe, 
  CalendarDays, PartyPopper, GraduationCap,
  Clock3, Calendar as CalendarIcon, 
  Building, AlertCircle, Video,
  Grid, BookOpen, Briefcase, Network,
  Trophy, ChevronLeft, Sparkles, Filter,
  X, ChevronRight, ArrowUpDown,
  SlidersHorizontal
} from 'lucide-react';
import EventCalendar from '../components/EventCalendar';
import HeroSection from '../components/HeroSection';

const EVENT_CATEGORIES = [
  {
    id: 'professional',
    title: "Training and Mentorship",
    description: "Build your career with expert-led sessions",
    types: ['workshop', 'training', 'career_fair', 'mentorship','seminar', 'webinar', 'panel_discussion', 'conference'],
    icon: Briefcase,
    gradient: "from-blue-500 to-cyan-600",
    bgGradient: "from-blue-50 to-cyan-50"
  },
  
  {
    id: 'networking',
    title: "Reunions and Networking",
    description: "Network with fellow alumni",
    types: ['reunion', 'social_gathering', 'networking'],
    icon: Network,
    gradient: "from-green-500 to-emerald-600",
    bgGradient: "from-green-50 to-emerald-50"
  },
  {
    id: 'special',
    title: "Special Events",
    description: "Celebrate achievements and milestones",
    types: ['award_ceremony', 'orientation', 'hackathon'],
    icon: Trophy,
    gradient: "from-orange-500 to-red-600",
    bgGradient: "from-orange-50 to-red-50"
  }
];
// Add this near the top of the file, before any card components
const BatchIndicator = ({ targetBatch }) => {
  if (!targetBatch?.year) return null;
  
  return (
    <div className="flex items-center gap-2 mt-2 bg-blue-50 px-2 py-1 rounded-full">
      <GraduationCap className="w-4 h-4 text-blue-500" />
      <span className="text-xs font-medium text-blue-600">
        {targetBatch.description || `Batch ${targetBatch.year}`}
      </span>
    </div>
  );
};
// Enhanced Advanced Filters with working functionality
const AdvancedFilters = ({ filters, onChange, onClose }) => {
  const [localFilters, setLocalFilters] = useState(filters);

  const handleApplyFilters = () => {
    onChange(localFilters);
    onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="fixed inset-0 z-50 overflow-y-auto bg-black/50"
      onClick={onClose}
    >
      <div 
        className="min-h-screen flex items-center justify-center p-4"
        onClick={e => e.stopPropagation()}
      >
        <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <SlidersHorizontal className="w-5 h-5 text-blue-600" />
              <h2 className="text-xl font-bold">Advanced Filters</h2>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Event Type */}
            <div>
              <label className="block text-sm font-medium mb-2">Event Type</label>
              <select
                value={localFilters.type}
                onChange={(e) => setLocalFilters({ ...localFilters, type: e.target.value })}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Types</option>
                <option value="workshop">Workshop</option>
                <option value="training">Training</option>
                <option value="seminar">Seminar</option>
                <option value="reunion">Reunion</option>
                <option value="networking">Networking</option>
                <option value="career_fair">Career Fair</option>
                <option value="panel_discussion">Panel Discussion</option>
              </select>
            </div>

            {/* Event Format */}
            <div>
              <label className="block text-sm font-medium mb-2">Event Format</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={localFilters.isVirtual}
                    onChange={(e) => setLocalFilters({
                      ...localFilters,
                      isVirtual: e.target.checked
                    })}
                    className="rounded text-blue-600"
                  />
                  <span>Virtual</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={localFilters.isPhysical}
                    onChange={(e) => setLocalFilters({
                      ...localFilters,
                      isPhysical: e.target.checked
                    })}
                    className="rounded text-blue-600"
                  />
                  <span>In-Person</span>
                </label>
              </div>
            </div>

            {/* Date Range */}
            <div>
              <label className="block text-sm font-medium mb-2">Date Range</label>
              <div className="flex gap-4">
                <input
                  type="date"
                  value={localFilters.dateRange.start}
                  onChange={(e) => setLocalFilters({
                    ...localFilters,
                    dateRange: { ...localFilters.dateRange, start: e.target.value }
                  })}
                  className="w-full p-2 border rounded-lg"
                />
                <input
                  type="date"
                  value={localFilters.dateRange.end}
                  onChange={(e) => setLocalFilters({
                    ...localFilters,
                    dateRange: { ...localFilters.dateRange, end: e.target.value }
                  })}
                  className="w-full p-2 border rounded-lg"
                />
              </div>
            </div>

            {/* Price Range */}
            <div>
              <label className="block text-sm font-medium mb-2">Price Range</label>
              <div className="flex items-center gap-4">
                <input
                  type="number"
                  placeholder="Min"
                  value={localFilters.priceRange?.min || ''}
                  onChange={(e) => setLocalFilters({
                    ...localFilters,
                    priceRange: { ...localFilters.priceRange, min: e.target.value }
                  })}
                  className="w-full p-2 border rounded-lg"
                />
                <span>to</span>
                <input
                  type="number"
                  placeholder="Max"
                  value={localFilters.priceRange?.max || ''}
                  onChange={(e) => setLocalFilters({
                    ...localFilters,
                    priceRange: { ...localFilters.priceRange, max: e.target.value }
                  })}
                  className="w-full p-2 border rounded-lg"
                />
              </div>
            </div>

            {/* Registration Status */}
            <div>
              <label className="block text-sm font-medium mb-2">Registration Status</label>
              <select
                value={localFilters.registrationStatus}
                onChange={(e) => setLocalFilters({
                  ...localFilters,
                  registrationStatus: e.target.value
                })}
                className="w-full p-2 border rounded-lg"
              >
                <option value="">All Statuses</option>
                <option value="open">Open</option>
                <option value="closing_soon">Closing Soon</option>
                <option value="closed">Closed</option>
              </select>
            </div>

            {/* Capacity */}
            <div>
              <label className="block text-sm font-medium mb-2">Capacity Status</label>
              <select
                value={localFilters.capacityStatus}
                onChange={(e) => setLocalFilters({
                  ...localFilters,
                  capacityStatus: e.target.value
                })}
                className="w-full p-2 border rounded-lg"
              >
                <option value="">All</option>
                <option value="available">Spots Available</option>
                <option value="filling_fast">Filling Fast</option>
                <option value="full">Full</option>
              </select>
            </div>
          </div>

          <div className="flex items-center justify-end gap-4 pt-4 border-t">
            <button
              onClick={() => {
                setLocalFilters({
                  type: '',
                  isVirtual: false,
                  isPhysical: false,
                  dateRange: { start: '', end: '' },
                  priceRange: { min: '', max: '' },
                  registrationStatus: '',
                  capacityStatus: ''
                });
              }}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
            >
              Reset Filters
            </button>
            <button
              onClick={handleApplyFilters}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};


const EnhancedCarousel = ({ items, renderItem, itemsPerPage = { desktop: 3, mobile: 1 } }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const totalPages = Math.ceil(items.length / itemsPerPage.desktop);
  const isMobileView = window.innerWidth < 768;
  const currentItemsPerPage = isMobileView ? itemsPerPage.mobile : itemsPerPage.desktop;

  const nextSlide = () => {
    setCurrentIndex((prev) => 
      prev + currentItemsPerPage >= items.length ? 0 : prev + currentItemsPerPage
    );
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => 
      prev - currentItemsPerPage < 0 ? 
      Math.max(0, items.length - currentItemsPerPage) : 
      prev - currentItemsPerPage
    );
  };

  return (
    <div className="relative">
      <div className="overflow-hidden">
        <div className="relative flex items-center">
          <button
            onClick={prevSlide}
            className="absolute left-0 z-10 p-2 rounded-full bg-white shadow-lg hover:bg-gray-50 -translate-x-1/2"
          >
            <ChevronLeft className="w-6 h-6 text-gray-600" />
          </button>
          
          <div className="flex transition-transform duration-300 ease-in-out w-full"
               style={{ transform: `translateX(-${(currentIndex / items.length) * 100}%)` }}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full">
              {items
                .slice(currentIndex, currentIndex + currentItemsPerPage)
                .map((item, idx) => (
                  <div key={idx} className="w-full">
                    {renderItem(item)}
                  </div>
                ))}
            </div>
          </div>

          <button
            onClick={nextSlide}
            className="absolute right-0 z-10 p-2 rounded-full bg-white shadow-lg hover:bg-gray-50 translate-x-1/2"
          >
            <ChevronRight className="w-6 h-6 text-gray-600" />
          </button>
        </div>
      </div>
      
      <div className="flex justify-center mt-4 gap-2">
        {Array.from({ length: totalPages }).map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrentIndex(idx * currentItemsPerPage)}
            className={`w-2 h-2 rounded-full transition-all ${
              Math.floor(currentIndex / currentItemsPerPage) === idx 
                ? 'bg-blue-600 w-4' 
                : 'bg-gray-300'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

// Professional Development Card
const ProfessionalCard = ({ event, onView, onDelete, currentAlumni }) => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true
  });
// Combine conditions: either the user created the event OR they're in the target batch
const canViewEvent = (
  // Event is open to all batches
  !event.registration?.targetBatch?.year ||
  // Event is for user's batch
  event.registration?.targetBatch?.year === currentAlumni?.graduationYear ||
  // User is the event creator
  (event.organizer.type === 'Alumni' && event.organizer.organizedBy._id === currentAlumni?._id)
);

// Check if user is the organizer
const isOrganizer = event.organizer.type === 'Alumni' && 
                   event.organizer.organizedBy._id === currentAlumni?._id;

// Don't render the card if user can't view it
if (!canViewEvent) return null;
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.5 }}
      className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
    >
      <div className="relative h-48 bg-gradient-to-r from-blue-500 to-cyan-500">
        {event.images?.[0] ? (
          <img
            src={event.images[0]}
            alt={event.title}
            className="w-full h-full object-cover opacity-90"
          />
        ) : (
          <div className="flex items-center justify-center h-full">
            <Briefcase className="w-12 h-12 text-white/80" />
          </div>
        )}
        <div className="absolute top-4 right-4">
          <span className="px-3 py-1 bg-white/90 rounded-full text-sm font-medium text-blue-600">
            {event.registration.fee.amount === 0 ? 'Free' : `₹${event.registration.fee.amount}`}
          </span>
        </div>
      </div>
      <div className="mb-4">
  <BatchIndicator targetBatch={event.registration.targetBatch} />
</div>
      <div className="p-6">
        <div className="flex items-center gap-2 mb-3">
          <div className="flex items-center gap-1">
            <CalendarDays className="w-4 h-4 text-blue-500" />
            <span className="text-sm text-gray-600">
              {new Date(event.schedule.startDate).toLocaleDateString()}
            </span>
          </div>
          <span className="text-gray-300">•</span>
          <div className="flex items-center gap-1">
            {event.venue.isVirtual ? (
              <>
                <Video className="w-4 h-4 text-blue-500" />
                <span className="text-sm text-gray-600">Virtual</span>
              </>
            ) : (
              <>
                <MapPin className="w-4 h-4 text-blue-500" />
                <span className="text-sm text-gray-600">{event.venue.city || 'Venue TBA'}</span>
              </>
            )}
          </div>
        </div>

        <h3 className="text-xl font-bold text-gray-900 mb-2">{event.title}</h3>
        <p className="text-gray-600 text-sm mb-4 line-clamp-2">{event.description}</p>

        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <div className="flex items-center gap-3">
            {event.organizer.organizedBy.profileImage ? (
              <img
                src={event.organizer.organizedBy.profileImage}
                alt={event.organizer.organizedBy.firstName}
                className="w-8 h-8 rounded-full object-cover"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                <GraduationCap className="w-4 h-4 text-blue-600" />
              </div>
            )}
            <div className="text-sm">
              <p className="font-medium text-gray-900">
                {event.organizer.organizedBy.firstName} {event.organizer.organizedBy.lastName}
              </p>
              <p className="text-gray-500">Organizer</p>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => onView(event._id)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              View Details
            </button>
            {isOrganizer && (
              <button
                onClick={() => onDelete(event._id)}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Knowledge Exchange Card
const EducationalCard = ({ event, onView, onDelete, currentAlumni }) => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true
  });
  const canViewEvent = (
    // Event is open to all batches
    !event.registration?.targetBatch?.year ||
    // Event is for user's batch
    event.registration?.targetBatch?.year === currentAlumni?.graduationYear ||
    // User is the event creator
    (event.organizer.type === 'Alumni' && event.organizer.organizedBy._id === currentAlumni?._id)
  );
  
  // Check if user is the organizer
  const isOrganizer = event.organizer.type === 'Alumni' && 
                     event.organizer.organizedBy._id === currentAlumni?._id;
  
  // Don't render the card if user can't view it
  if (!canViewEvent) return null;
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.5 }}
      className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
    >
      <div className="relative h-48 bg-gradient-to-r from-purple-500 to-pink-500">
        {event.images?.[0] ? (
          <img
            src={event.images[0]}
            alt={event.title}
            className="w-full h-full object-cover opacity-90"
          />
        ) : (
          <div className="flex items-center justify-center h-full">
            <BookOpen className="w-12 h-12 text-white/80" />
          </div>
        )}
        {event.agenda && event.agenda.length > 0 && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
            <div className="flex items-center gap-2">
              <PartyPopper className="w-4 h-4 text-white" />
              <span className="text-sm text-white font-medium">
                {event.agenda.length} Sessions Planned
              </span>
            </div>
          </div>
        )}
      </div>
      <div className="mb-4">
  <BatchIndicator targetBatch={event.registration.targetBatch} />
</div>
      <div className="p-6">
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-xl font-bold text-gray-900">{event.title}</h3>
          <span className="px-3 py-1 bg-purple-100 rounded-full text-sm font-medium text-purple-600">
            {event.registration.fee.amount === 0 ? 'Free' : `₹${event.registration.fee.amount}`}
          </span>
        </div>

        <div className="space-y-4 mb-6">
          <p className="text-gray-600 text-sm line-clamp-2">{event.description}</p>

          {event.agenda?.slice(0, 2).map((item, index) => (
            <div key={index} className="flex items-start gap-3">
              <div className="mt-1">
                <Clock3 className="w-4 h-4 text-purple-500" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">{item.title}</p>
                <p className="text-xs text-gray-500">
                  {new Date(item.time).toLocaleTimeString()}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <div className="flex items-center gap-3">
            {event.organizer.organizedBy.profileImage ? (
              <img
                src={event.organizer.organizedBy.profileImage}
                alt={event.organizer.organizedBy.firstName}
                className="w-8 h-8 rounded-full object-cover"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                <GraduationCap className="w-4 h-4 text-purple-600" />
              </div>
            )}
            <div className="text-sm">
              <p className="font-medium text-gray-900">
                {event.organizer.organizedBy.firstName} {event.organizer.organizedBy.lastName}
              </p>
              <p className="text-gray-500">Expert Speaker</p>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => onView(event._id)}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              Learn More
            </button>
            {isOrganizer && (
              <button
                onClick={() => onDelete(event._id)}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Networking Card
const NetworkingCard = ({ event, onView, onDelete, currentAlumni }) => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true
  });
  const canViewEvent = (
    // Event is open to all batches
    !event.registration?.targetBatch?.year ||
    // Event is for user's batch
    event.registration?.targetBatch?.year === currentAlumni?.graduationYear ||
    // User is the event creator
    (event.organizer.type === 'Alumni' && event.organizer.organizedBy._id === currentAlumni?._id)
  );
  
  // Check if user is the organizer
  const isOrganizer = event.organizer.type === 'Alumni' && 
                     event.organizer.organizedBy._id === currentAlumni?._id;
  
  // Don't render the card if user can't view it
  if (!canViewEvent) return null;
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.5 }}
      className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
    >
      <div className="relative h-48 bg-gradient-to-r from-green-500 to-emerald-500">
        {event.images?.[0] ? (
          <img
            src={event.images[0]}
            alt={event.title}
            className="w-full h-full object-cover opacity-90"
          />
        ) : (
          <div className="flex items-center justify-center h-full">
            <Network className="w-12 h-12 text-white/80" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        <div className="absolute bottom-4 left-4">
          <div className="flex items-center gap-2 text-white mb-1">
            <Users className="w-4 h-4" />
            <span className="text-sm">
              {event.capacity.max - event.capacity.remaining}/{event.capacity.max} Attending
            </span>
          </div>
        </div>
      </div>
      <div className="mb-4">
  <BatchIndicator targetBatch={event.registration.targetBatch} />
</div>
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-green-500" />
            <span className="text-sm text-gray-600">
              {new Date(event.schedule.startDate).toLocaleDateString()}
            </span>
          </div>
          <span className="px-3 py-1 bg-green-100 rounded-full text-sm font-medium text-green-600">
            {event.registration.fee.amount === 0 ? 'Free' : `₹${event.registration.fee.amount}`}
          </span>
        </div>

        <h3 className="text-xl font-bold text-gray-900 mb-2">{event.title}</h3>
        <p className="text-gray-600 text-sm mb-4 line-clamp-2">{event.description}</p>

        <div className="flex items-center gap-4 mb-4">
          {event.venue.isVirtual ? (
            <div className="flex items-center gap-2">
              <Video className="w-4 h-4 text-green-500" />
              <span className="text-sm text-gray-600">Virtual Networking</span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-green-500" />
              <span className="text-sm text-gray-600">{event.venue.name || 'Venue TBA'}</span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <div className="flex items-center gap-3">
            {event.organizer.organizedBy.profileImage ? (
              <img
                src={event.organizer.organizedBy.profileImage}
                alt={event.organizer.organizedBy.firstName}
                className="w-8 h-8 rounded-full object-cover"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                <GraduationCap className="w-4 h-4 text-green-600" />
              </div>
            )}
            <div className="text-sm">
              <p className="font-medium text-gray-900">
                {event.organizer.organizedBy.firstName} {event.organizer.organizedBy.lastName}
              </p>
              <p className="text-gray-500">Host</p>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => onView(event._id)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Join Network
            </button>
            {isOrganizer && (
              <button
                onClick={() => onDelete(event._id)}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Special Events Card
const SpecialCard = ({ event, onView, onDelete, currentAlumni }) => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true
  });
  const canViewEvent = (
    // Event is open to all batches
    !event.registration?.targetBatch?.year ||
    // Event is for user's batch
    event.registration?.targetBatch?.year === currentAlumni?.graduationYear ||
    // User is the event creator
    (event.organizer.type === 'Alumni' && event.organizer.organizedBy._id === currentAlumni?._id)
  );
  
  // Check if user is the organizer
  const isOrganizer = event.organizer.type === 'Alumni' && 
                     event.organizer.organizedBy._id === currentAlumni?._id;
  
  // Don't render the card if user can't view it
  if (!canViewEvent) return null;
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.5 }}
      className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden"
    >
      <div className="relative h-48 bg-gradient-to-r from-orange-500 to-red-500">
        {event.images?.[0] ? (
          <img
            src={event.images[0]}
            alt={event.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="flex items-center justify-center h-full">
            <Trophy className="w-12 h-12 text-white/80" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent" />
        <div className="absolute top-4 right-4">
          <span className="px-3 py-1 bg-white rounded-full text-sm font-medium text-orange-600">
            {event.registration.fee.amount === 0 ? 'Free Entry' : `₹${event.registration.fee.amount}`}
          </span>
        </div>
      </div>
      <div className="mb-4">
  <BatchIndicator targetBatch={event.registration.targetBatch} />
</div>
      <div className="p-6">
        <div className="flex items-center gap-2 mb-3">
          <span className="px-2 py-1 bg-orange-100 rounded-md text-xs font-medium text-orange-600">
            Special Event
          </span>
          <span className="text-gray-300">•</span>
          <div className="flex items-center gap-1">
            <Clock3 className="w-4 h-4 text-orange-500" />
            <span className="text-sm text-gray-600">
              {new Date(event.schedule.startDate).toLocaleDateString()}
            </span>
          </div>
        </div>

        <h3 className="text-xl font-bold text-gray-900 mb-2">{event.title}</h3>
        <p className="text-gray-600 text-sm mb-4 line-clamp-2">{event.description}</p>

        <div className="flex items-center gap-6 mb-4">
          {event.venue.isVirtual ? (
            <div className="flex items-center gap-2">
              <Video className="w-4 h-4 text-orange-500" />
              <span className="text-sm text-gray-600">Virtual Event</span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-orange-500" />
              <span className="text-sm text-gray-600">{event.venue.name || 'Venue TBA'}</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-orange-500" />
            <span className="text-sm text-gray-600">
              {event.capacity.max - event.capacity.remaining} spots filled
            </span>
          </div>
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <div className="flex items-center gap-3">
            {event.organizer.organizedBy.profileImage ? (
              <img
                src={event.organizer.organizedBy.profileImage}
                alt={event.organizer.organizedBy.firstName}
                className="w-8 h-8 rounded-full object-cover"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                <GraduationCap className="w-4 h-4 text-orange-600" />
              </div>
            )}
            <div className="text-sm">
              <p className="font-medium text-gray-900">
                {event.organizer.organizedBy.firstName} {event.organizer.organizedBy.lastName}
              </p>
              <p className="text-gray-500">Event Organizer</p>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => onView(event._id)}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
            >
              Register Now
            </button>
            {isOrganizer && (
              <button
                onClick={() => onDelete(event._id)}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const CategorySection = ({ category, events, onViewEvent, onDelete, currentAlumni }) => {
  if (!events || events.length === 0) return null;

  const CardComponent = {
    professional: ProfessionalCard,
    educational: EducationalCard,
    networking: NetworkingCard,
    special: SpecialCard
  }[category.id];

  return (
    <div className="mb-16">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between mb-8"
      >
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <div className={`p-3 rounded-xl bg-gradient-to-br ${category.bgGradient}`}>
              <category.icon className={`w-6 h-6 text-${category.gradient.split('-')[1]}-600`} />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">{category.title}</h2>
          </div>
          <p className="text-gray-600">{category.description}</p>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-500">{events.length} events</span>
         
        </div>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {events.slice(0, 3).map(event => (
          <CardComponent
            key={event._id}
            event={event}
            onView={onViewEvent}
            onDelete={onDelete}
            currentAlumni={currentAlumni} 
          />
        ))}
      </div>
    </div>
  );
};

const EventList = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentAlumni, setCurrentAlumni] = useState(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    type: '',
    status: '',
    timeRange: '',
    registrationStatus: '',
  });

  const [advancedFilters, setAdvancedFilters] = useState({
    type: '',
    isVirtual: false,
    isPhysical: false,
    dateRange: { start: '', end: '' },
    priceRange: { min: '', max: '' },
    registrationStatus: '',
    capacityStatus: '',
    sortBy: 'dateAsc'
  });

  useEffect(() => {
    const initialize = async () => {
      try {
        setLoading(true);
        await Promise.all([fetchEvents(), fetchCurrentAlumni()]);
      } catch (err) {
        console.error('Initialization error:', err);
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    initialize();
  }, [currentAlumni?.graduationYear]); // Add this dependency

  const fetchCurrentAlumni = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/alumni/me`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch alumni details');
      const data = await response.json();
      setCurrentAlumni(data);
    } catch (err) {
      console.error('Error fetching alumni details:', err);
      setError(err.message);
    }
  };
  const fetchEvents = async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `${process.env.REACT_APP_BASE_URL}/api/events/approved-events`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error('Failed to fetch events');
      const data = await response.json();
      
      // Debug logs
      console.log('Current Alumni:', currentAlumni?._id);
      console.log('Events Data:', data.map(event => ({
        title: event.title,
        organizerId: event.organizer?.organizedBy?._id,
        targetBatch: event.registration?.targetBatch?.year,
        isCreator: event.organizer?.organizedBy?._id === currentAlumni?._id
      })));
  
      setEvents(data);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError(err.message);
    }
  };
// Add batch indicator to each card component
const BatchIndicator = ({ targetBatch }) => {
  if (!targetBatch?.year) return null;
  
  return (
    <div className="flex items-center gap-2 mt-2 bg-blue-50 px-2 py-1 rounded-full">
      <GraduationCap className="w-4 h-4 text-blue-500" />
      <span className="text-xs font-medium text-blue-600">
        {targetBatch.description || `Batch ${targetBatch.year}`}
      </span>
    </div>
  );
};





  const handleDelete = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/events/${eventId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to delete event');
      await fetchEvents();
    } catch (err) {
      setError(err.message);
    }
  };

  const categorizedEvents = useMemo(() => {
    return EVENT_CATEGORIES.map(category => ({
      ...category,
      events: events.filter(event => category.types.includes(event.type))
    }));
  }, [events]);

  const getStats = useCallback(() => {
    const now = new Date();
    
    const activeEvents = events.filter(event => {
      const startDate = new Date(event.schedule.startDate);
      const endDate = new Date(event.schedule.endDate);
      return startDate <= now && endDate >= now && event.status !== 'cancelled';
    });
    
    const upcomingEvents = events.filter(event => {
      const startDate = new Date(event.schedule.startDate);
      return startDate > now && event.status !== 'cancelled';
    });
    
    const virtualEvents = events.filter(event => event.venue.isVirtual);
    
    return {
      total: events.length,
      active: activeEvents.length,
      upcoming: upcomingEvents.length,
      virtual: virtualEvents.length
    };
  }, [events]);

  const stats = getStats();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <HeroSection
        title="Campus Events & Meetups"
        subtitle={`Discover ${stats.upcoming} upcoming events and ${stats.virtual} virtual gatherings`}
        showSearch={true}
        searchConfig={{
          value: filters.search,
          onChange: (e) => setFilters({ ...filters, search: e.target.value }),
          placeholder: "Search events by title, type, or organizer..."
        }}
        statsConfig={[
          { value: stats.active, label: "Active Events" },
          { value: stats.upcoming, label: "Upcoming Events" },
          { value: stats.virtual, label: "Virtual Events" },
          { value: events.length, label: "Total Events" }
        ]}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Action Bar */}
        <div className="flex items-center justify-between mb-8 gap-4">
          <div className="flex items-center gap-4">
             <button
              onClick={() => setIsCalendarOpen(true)}
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <CalendarIcon className="h-5 w-5 mr-2" />
              View Calendar
            </button>
            {/* <button
              onClick={() => setIsFiltersOpen(true)}
              className="inline-flex items-center px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Filter className="w-5 h-5 mr-2 text-blue-600" />
              Filters
            </button> */}
          </div>

          <button
            onClick={() => navigate('/events/create')}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5 mr-2" />
            Create Event
          </button>
        </div>

        {/* Advanced Filters Modal */}
        <AnimatePresence>
          {isFiltersOpen && (
            <AdvancedFilters
              filters={advancedFilters}
              onChange={setAdvancedFilters}
              onClose={() => setIsFiltersOpen(false)}
            />
          )}
        </AnimatePresence>

        {/* Calendar Modal */}
        <EventCalendar
          events={events}
          isOpen={isCalendarOpen}
          onClose={() => setIsCalendarOpen(false)}
          onEventClick={(id) => navigate(`/events/${id}`)}
        />

        {/* Categorized Events */}
        {categorizedEvents.map(category => (
          <CategorySection
            key={category.id}
            category={category}
            events={category.events}
            onViewEvent={(id) => navigate(`/events/${id}`)}
            onDelete={handleDelete}
            currentAlumni={currentAlumni}
          />
        ))}

        {/* Empty State */}
        {events.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
          >
            <div className="bg-white rounded-xl shadow-sm p-8 max-w-lg mx-auto">
              <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900">No events found</h3>
              <p className="mt-2 text-gray-500">
                {filters.search
                  ? "No events match your search criteria. Try adjusting your filters."
                  : "There are no events scheduled at the moment."}
              </p>
              <button
                onClick={() => navigate('/events/create')}
                className="mt-6 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="h-5 w-5 mr-2" />
                Create New Event
              </button>
            </div>
          </motion.div>
        )}
      </div>

      {/* Action Banner */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-8 mb-12">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="text-white mb-4 md:mb-0">
              <h3 className="text-xl font-semibold mb-2">
                Want to organize new events or provide mentorship?
              </h3>
              <p className="text-blue-100">
                Share your knowledge and experiences with fellow alumni and students.
              </p>
            </div>
            <button
              onClick={() => navigate('/events/create')}
              className="inline-flex items-center px-6 py-3 border border-transparent 
                text-base font-medium rounded-xl text-blue-700 bg-white 
                hover:bg-blue-50 transition-colors"
            >
              <Plus className="h-5 w-5 mr-2" />
              Create a Event
            </button>
          </div>
        </div>
    </div>
  );
};

export default EventList;